import type {
  TesterPresenceItem,
  TesterMergedScheduleResponse,
} from '@runhuman/shared';
import { computeScheduleWindow } from '@runhuman/shared';
import { ScheduleStrip } from './ScheduleStrip';

interface MainTabProps {
  readonly schedule: TesterMergedScheduleResponse | undefined;
  readonly scheduleLoading: boolean;
  readonly tester: TesterPresenceItem;
}

/**
 * Activity-side mirror of `<InternalTesterProfileDialog>` MainTab. Two
 * sections: the next-24-hours schedule strip (driven by the same shared
 * `computeScheduleWindow` helper the web dialog uses) and a status line
 * — either "In job: <orgname>" when the tester is currently working, or
 * "Back on shift in <Xh Ym>" / "Not currently in a job."
 *
 * No "Watch live" CTA in the Activity — that's a web-only admin power.
 */
export function MainTab({ schedule, scheduleLoading, tester }: MainTabProps) {
  return (
    <div className="profile-modal__main">
      <Section title="Next 24 hours">
        {scheduleLoading || !schedule ? (
          <div className="profile-modal__loading">Loading…</div>
        ) : (
          <ScheduleStrip cells={computeScheduleWindow(schedule, new Date()).cells} />
        )}
      </Section>

      <Section title={tester.activeJob ? 'In job' : 'Status'}>
        {tester.activeJob ? (
          <ActiveJobLine activeJob={tester.activeJob} />
        ) : (
          <StatusLine schedule={schedule} availability={tester.availability} />
        )}
      </Section>
    </div>
  );
}

function ActiveJobLine({
  activeJob,
}: {
  activeJob: NonNullable<TesterPresenceItem['activeJob']>;
}) {
  if (!activeJob.project) {
    return <p className="profile-modal__status">Currently testing a demo job.</p>;
  }
  return (
    <p className="profile-modal__status">
      Currently testing for <strong>{activeJob.project.organizationName}</strong>
      {' — '}
      {activeJob.project.name}.
    </p>
  );
}

function StatusLine({
  schedule,
  availability,
}: {
  schedule: TesterMergedScheduleResponse | undefined;
  availability: TesterPresenceItem['availability'];
}) {
  if (availability === 'offline' && schedule) {
    const { nextScheduledAt } = computeScheduleWindow(schedule, new Date());
    if (nextScheduledAt) {
      return (
        <p className="profile-modal__status profile-modal__status--muted">
          Back on shift in {formatUntil(nextScheduledAt, new Date())}
        </p>
      );
    }
  }
  return (
    <p className="profile-modal__status profile-modal__status--muted">
      Not currently in a job.
    </p>
  );
}

function formatUntil(future: Date, now: Date): string {
  const diffMs = future.getTime() - now.getTime();
  const totalMinutes = Math.max(0, Math.round(diffMs / 60000));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours === 0) return `${minutes}m`;
  if (minutes === 0) return `${hours}h`;
  return `${hours}h ${minutes}m`;
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h3 className="profile-modal__section-title">{title}</h3>
      {children}
    </section>
  );
}
